import Button from "../core/Button";
import ResizableScene from "../core/ResizableScene";
import F84Game from "../Game";

// enum LiveRegionType {
// 	Polite = "polite",
// 	Assertive = "assertive",
// 	Off = "off",
// }

// Use this to shorten the parameter for the Phaser Object
// Includes image, sprite, adn container in case we have interactive elements that don't extend from Button
type PhaserElement = Phaser.GameObjects.Text | Phaser.GameObjects.Image | Phaser.GameObjects.Sprite | Phaser.GameObjects.Container | Button;

export class ARIAElement {
	public phaserElement?: PhaserElement;
	private camera?: Phaser.Cameras.Scene2D.Camera;
	public HTMLElement: HTMLElement;
	public properties: Map<string, any> = new Map<string, any>();

	constructor(element: HTMLElement, object?: PhaserElement) {
		this.HTMLElement = element;
		this.phaserElement = object;
		this.properties = new Map<string, any>();
		element.style.position = "absolute";
		if (this.phaserElement) {
			this.camera = this.phaserElement.scene.cameras.cameras.find((c) => c.renderList.includes(this.phaserElement));
		}
		this.resize();
	}

	public update(): void {
		if (this.phaserElement && !this.HTMLElement.parentElement.hidden) {
			this.resize();
		}
	}

	isActiveElement(): boolean {
		return document.activeElement == this.HTMLElement;
	}

	show(): void {
		this.HTMLElement.hidden = false;
		this.HTMLElement.style.opacity = "1";
	}

	showLayer(layer?: string, hideRest = false): void {
		ARIA.showLayer(layer, hideRest);
	}

	hideLayer(layer?: string): void {
		ARIA.hideLayer(layer);
	}

	hide(): void {
		this.HTMLElement.hidden = true;
		this.HTMLElement.style.opacity = "0";
	}

	blur(): void {
		this.HTMLElement.blur();
	}

	focus(): void {
		this.HTMLElement.focus();
	}

	setText(text: string): void {
		//text = text.replace(/\n+/g, " ");
		this.HTMLElement.innerText = text;
		this.HTMLElement.setAttribute("aria-label", text);
		this.resize();
	}

	getText(): string {
		return this.HTMLElement.innerText;
	}

	private getWorldPosition(): Phaser.Math.Vector2 {
		const obj = this.phaserElement;
		let position = new Phaser.Math.Vector2(0, 0);
		if (obj) {
			position.set(obj.x, obj.y);
			if (obj.parentContainer) {
				position = obj.parentContainer.getWorldTransformMatrix().transformPoint(obj.x, obj.y, position) as Phaser.Math.Vector2;
			}

			const transform = obj.getWorldTransformMatrix();
			// TODO: Figure out a way to account for other cameras rendering the Game Object
			position.x -= transform.scaleX * obj.width * obj.originX - (this.camera ? this.camera.scrollX : 0);
			position.y -= transform.scaleY * obj.height * obj.originY - (this.camera ? this.camera.scrollY : 0);
			position.x *= ARIA.viewportScale.x;
			position.y *= ARIA.viewportScale.y;
		}
		return position;
	}

	private getSize(): Phaser.Math.Vector2 {
		const obj = this.phaserElement;
		const size = new Phaser.Math.Vector2();
		if (obj) {
			if (obj.parentContainer) {
				const transform = obj.getWorldTransformMatrix();
				size.x = transform.scaleX * obj.width * ARIA.viewportScale.x;
				size.y = transform.scaleY * obj.height * ARIA.viewportScale.y;
			} else {
				size.set(obj.displayWidth * ARIA.viewportScale.x, obj.displayHeight * ARIA.viewportScale.y);
			}
		} else {
			size.x = Number.parseFloat(this.HTMLElement.parentElement.parentElement.style.width.replace("px", ""));
			size.y = Number.parseFloat(this.HTMLElement.parentElement.parentElement.style.height.replace("px", ""));
		}
		return size;
	}

	public resize(): void {
		const position = this.getWorldPosition();
		const size = this.getSize();
		this.HTMLElement.style.left = position.x + "px";
		this.HTMLElement.style.top = position.y + "px";

		const useCanvasWidth = this.properties.get("useCanvasWidth") as boolean;
		const width = useCanvasWidth ? this.HTMLElement.parentElement.parentElement.style.width : size.x + "px";
		this.HTMLElement.style.width = width;
		this.HTMLElement.style.height = size.y + "px";
	}
}

type ARIALayer = {
	container: HTMLDivElement;
	elements: ARIAElement[];
};

export default class ARIA {
	static readonly DEFAULT_LAYER = "default";

	private static _container: HTMLDivElement;
	// private static _popup: HTMLDivElement;
	private static _resolution: number;
	private static _viewportScale: Phaser.Math.Vector2;
	public static get viewportScale(): Phaser.Math.Vector2 {
		return this._viewportScale;
	}

	private static _layers: Map<string, ARIALayer>;

	// private static _liveRegions: Map<string, HTMLElement>;
	// private static _elements: ARIAElement;

	private static _millisecondsBetweenUpdates = 1000;
	private static _nextUpdateTime = 0;

	// public static onPositionMoved: Phaser.Events.EventEmitter;

	private static _initialized = false;

	public static init(): void {
		if (this._initialized) return;

		this._resolution = F84Game.Instance.quality.resolution;
		this._viewportScale = new Phaser.Math.Vector2(1, 1);

		this._container = window.document.createElement("div");
		// if (F84Game.Instance.device.android) this._container.setAttribute("role", "navigation");
		this._container.style.position = "absolute";
		this._container.style.top = 0 + "px";
		this._container.style.left = 0 + "px";
		// TODO: figure out percise genaric measurements for game width and height
		this._container.style.width = "100%"; // F84Game.Instance.width * scaleRatio + "px";
		this._container.style.height = "100%"; // F84Game.Instance.scene * scaleRatio + "px";
		this._container.style.zIndex = "-9999";

		this._layers = new Map<string, ARIALayer>();
		// add default div element
		this.addLayer(this.DEFAULT_LAYER);

		window.document.body.appendChild(this._container);

		// this._liveRegions = new Map<string, HTMLElement>();

		// this.onPositionMoved = new Phaser.Events.EventEmitter();

		// window.onscroll = (): void => {
		// 	let scrollTop = window.document.body.scrollTop;
		// 	this.onPositionMoved.dispatch(scrollTop, scrollTop / this._scaleRatio);
		// };

		F84Game.Instance.events.on(Phaser.Core.Events.POST_STEP, this.update, this);
		this._initialized = true;
	}

	public static addLayer(name: string): void {
		if (this._layers.has(name)) return;
		const div = window.document.createElement("div");
		div.style.position = "absolute";
		div.style.top = "0px";
		div.style.left = "0px";
		div.style.width = "100%"; // F84Game.Instance.width * scaleRatio + "px";
		div.style.height = "100%";

		const layer: ARIALayer = {
			container: div,
			elements: new Array<ARIAElement>(),
		};

		this._container.appendChild(div);
		this._layers.set(name, layer);
	}

	public static removeLayer(name: string): void {
		if (!this._layers.has(name) || name == "default") return;

		const layer = this._layers.get(name);
		if (name == this.DEFAULT_LAYER) {
			// Remove all children from the default layer
			while (layer.container.children && layer.container.children.length > 0) {
				const element = layer.container.removeChild(layer.container.children[0]);
				element.innerHTML = "";
			}
		} else {
			this._container.removeChild(layer.container);
			this._layers.delete(name);
		}
	}

	public static addElement(element: ARIAElement, layer?: string): void {
		if (!layer) layer = this.DEFAULT_LAYER;
		if (!this._layers.has(layer)) this.addLayer(layer);
		const layerObj = this._layers.get(layer);
		layerObj.container.appendChild(element.HTMLElement);
		layerObj.elements.push(element);
	}

	public static removeElement(object: PhaserElement | ARIAElement): void {
		const element = object instanceof ARIAElement ? object : this.getARIAElement(object);
		const keys = Array.from(this._layers.keys());
		for (const key of keys) {
			const layer = this._layers.get(key);
			const index = layer.elements.indexOf(element);
			if (index >= 0) {
				layer.elements.splice(index, 1);
				layer.container.removeChild(element.HTMLElement);
				if (key != this.DEFAULT_LAYER && layer.elements.length <= 0) this.removeLayer(key);
				return;
			}
		}
	}

	public static resizeLayer(layer: string): void {
		if (this._layers.has(layer)) {
			const layerObj = this._layers.get(layer);
			layerObj.elements.forEach((e) => e.resize());
		}
	}

	public static resize(scene: ResizableScene): void {
		if (!this._initialized) {
			this.init();
		}
		// else {
		// 	// this._resolution = F84Game.Instance.quality.resolution;
		// }

		this._container.style.width = scene.game.canvas.style.width; // scene.viewport.width + "px"; // F84Game.Instance.width * this._scaleRatio + "px";
		this._container.style.height = scene.game.canvas.style.height; // scene.viewport.height + "px"; // F84Game.Instance.height * this._scaleRatio + "px";
		// this._popup.style.width = scene.game.canvas.style.width; // scene.viewport.width + "px"; // Game.Instance.width * this._scaleRatio + "px";
		// this._popup.style.height = scene.game.canvas.style.height; // viewport.height + "px"; // Game.Instance.height * this._scaleRatio + "px";

		this._viewportScale.x = Number.parseFloat(scene.game.canvas.style.width.replace("px", "")) / scene.viewport.width;
		this._viewportScale.y = Number.parseFloat(scene.game.canvas.style.height.replace("px", "")) / scene.viewport.height;

		// Update element position and size on resize
		for (const key of Array.from(this._layers.keys())) {
			this.resizeLayer(key);
		}
	}

	public static showLayer(layer?: string, hideRest = false): void {
		if (!layer) layer = this.DEFAULT_LAYER;
		if (!this._layers.has(layer)) return;
		const showLayer = this._layers.get(layer);
		for (const element of showLayer.elements) {
			// resize element before it's shown
			element.resize();
			element.show();
		}

		if (hideRest) {
			const keys = Array.from(this._layers.keys());
			for (const key of keys) {
				if (key != layer) this.hideLayer(key);
			}
		}
	}

	public static hideLayer(layer?: string): void {
		if (!layer) layer = this.DEFAULT_LAYER;
		if (!this._layers.has(layer)) return;
		const hideLayer = this._layers.get(layer);
		for (const element of hideLayer.elements) {
			element.hide();
		}
	}

	public static update(time: number, delta: number): void {
		this._nextUpdateTime -= delta;
		if (this._nextUpdateTime > 0) return;
		this._nextUpdateTime = this._millisecondsBetweenUpdates;

		const keys = Array.from(this._layers.keys());
		for (const key of keys) {
			const layer = this._layers.get(key);
			for (const element of layer.elements) {
				element.update();
			}
		}
	}

	public static addButton(
		button: PhaserElement,
		text: string,
		layer?: string,
		focus?: boolean,
		useOnce = false,
		setUpdateNull = false,
		followCanvas = false,
		useCanvasWidth = false
	): ARIAElement {
		// if (!F84Game.Instance.accessibilityEnabled) return null;

		const buttonElement = window.document.createElement("button");
		buttonElement.innerText = text;
		buttonElement.tabIndex = 0;

		buttonElement.setAttribute("aria-label", text);

		const ariaElement = new ARIAElement(buttonElement, button);
		ariaElement.properties.set("setUpdateNull", setUpdateNull);
		ariaElement.properties.set("followCanvas", followCanvas);
		ariaElement.properties.set("useCanvasWidth", useCanvasWidth);
		ariaElement.properties.set("shouldUpdate", true);
		if (useCanvasWidth) ariaElement.resize();
		this.addElement(ariaElement, layer);

		buttonElement.onclick = (e: Event): void => {
			e.stopPropagation();
			e.preventDefault();
			// Only trigger button events if the button is enabled
			if (button.input && button.input.enabled) {
				// TODO: Handle Cases of unique buttons that only care about pointer down events
				button.emit(Phaser.Input.Events.GAMEOBJECT_POINTER_UP);
				if (useOnce) {
					this.removeElement(ariaElement);
					buttonElement.innerHTML = "";
				}
			}
		};

		button.once(Phaser.GameObjects.Events.DESTROY, (): void => {
			buttonElement.onclick = null;
			this.removeElement(ariaElement);
		});

		if (focus) {
			buttonElement.focus();
		}

		return ariaElement;
	}

	public static addPTag(label: string): HTMLParagraphElement {
		// if (!F84Game.Instance.accessibilityEnabled) return null;

		const element: HTMLParagraphElement = window.document.createElement("p");
		element.setAttribute("aria-label", label);
		element.innerText = label;
		this._container.appendChild(element);
		element.tabIndex = 0;
		element.style.position = "absolute";
		element.style.width = "100%";
		element.style.height = "100%";
		element.style.textAlign = "center";

		element.focus();

		return element;
	}

	public static addTextGroup(data: {
		container: PhaserElement;
		textField: Phaser.GameObjects.Text;
		setText?: (text: string) => string;
		layer?: string;
		focus?: boolean;
	}): ARIAElement {
		const element: HTMLDivElement = window.document.createElement("div");
		const text = data.setText ? data.setText(data.textField.text) : data.textField.text; // .replace(/\n+/g, " ");
		element.setAttribute("aria-label", text);
		element.innerText = text;
		element.tabIndex = 0;
		element.style.padding = "0";
		element.style.margin = "0";

		const fontScale = window.devicePixelRatio;
		if (typeof data.textField.style.fontSize === "string") {
			const size = parseFloat(data.textField.style.fontSize.replace("px", ""));
			element.style.fontSize = size / fontScale + "px";
		} else if (typeof data.textField.style.fontSize === "number") {
			element.style.fontSize = data.textField.style.fontSize / fontScale + "px";
		}
		element.style.overflow = "hidden";
		element.style.textAlign = "center";

		const ariaElement = new ARIAElement(element, data.container);
		this.addElement(ariaElement, data.layer);

		data.textField.on("textUpdated", (text: string) => {
			ariaElement.setText(data.setText ? data.setText(text) : text);
		});

		data.container.once(Phaser.GameObjects.Events.DESTROY, (): void => {
			this.removeElement(ariaElement);
		});

		if (data.focus == true) {
			element.focus();
		}

		return ariaElement;
	}

	public static addTextWithObject(object: PhaserElement, text: string, layer?: string, focus = false): ARIAElement {
		// if (!F84Game.Instance.accessibilityEnabled) return null;

		const element: HTMLDivElement = window.document.createElement("div");
		element.setAttribute("aria-label", text);
		element.innerText = text;
		element.tabIndex = 0;
		element.style.padding = "0";
		element.style.margin = "0";
		element.style.position = "absolute";

		const fontScale = window.devicePixelRatio;
		element.style.fontSize = 32 / fontScale + "px";
		element.style.overflow = "hidden";
		element.style.textAlign = "center";
		element.style.verticalAlign = "middle";

		const ariaElement = new ARIAElement(element, object);
		this.addElement(ariaElement, layer);

		object.once(Phaser.GameObjects.Events.DESTROY, () => {
			this.removeElement(ariaElement);
		});

		if (focus) {
			element.focus();
		}

		return ariaElement;
	}

	public static addText(textField: Phaser.GameObjects.Text, layer: string, focus?: boolean): ARIAElement {
		// if (!F84Game.Instance.accessibilityEnabled) return null;

		const element: HTMLDivElement = window.document.createElement("div");
		const text = textField.text; // .replace(/\n+/g, " ");
		element.setAttribute("aria-label", text);
		element.innerText = text;
		element.tabIndex = 0;
		element.style.padding = "0";
		element.style.margin = "0";

		const fontScale = window.devicePixelRatio;
		if (typeof textField.style.fontSize === "string") {
			const size = parseFloat(textField.style.fontSize.replace("px", ""));
			element.style.fontSize = size / fontScale + "px";
		} else if (typeof textField.style.fontSize === "number") {
			element.style.fontSize = textField.style.fontSize / fontScale + "px";
		}
		element.style.overflow = "hidden";
		element.style.textAlign = "center";

		const ariaElement = new ARIAElement(element, textField);
		this.addElement(ariaElement, layer);

		textField.once(Phaser.GameObjects.Events.DESTROY, (): void => {
			this.removeElement(ariaElement);
		});

		if (focus) {
			element.focus();
		}

		return ariaElement;
	}

	// public static addLiveRegion(key: string, regionType: LiveRegionType = LiveRegionType.Polite): HTMLElement {
	// 	// if (!F84Game.Instance.accessibilityEnabled) return null;

	// 	if (this._liveRegions.has(key)) {
	// 		return this._liveRegions.get(key);
	// 	}

	// 	const element: HTMLDivElement = window.document.createElement("div");
	// 	element.setAttribute("aria-live", regionType);
	// 	this._container.appendChild(element);

	// 	this._liveRegions.set(key, element);

	// 	return element;
	// }

	// public static removeLiveRegion(key: string): boolean {
	// 	// if (!F84Game.Instance.accessibilityEnabled) return false;

	// 	if (this._liveRegions.has(key)) {
	// 		const element = this._liveRegions.get(key);
	// 		this._container.removeChild(element);
	// 		element.innerHTML = "";
	// 		this._liveRegions.delete(key);
	// 		return true;
	// 	}

	// 	return false;
	// }

	// public static setLiveRegion(key: string, text: string): boolean {
	// 	// if (!F84Game.Instance.accessibilityEnabled) return false;

	// 	if (this._liveRegions.has(key)) {
	// 		this._liveRegions.get(key).innerText = text;
	// 		return true;
	// 	}

	// 	return false;
	// }

	public static clear(): void {
		// if (!F84Game.Instance.accessibilityEnabled) return;

		if (this._layers.size > 0) {
			const keys = Array.from(this._layers.keys());
			for (const key of keys) {
				this.removeLayer(key);
			}
		}
	}

	private static getARIAElement(object: PhaserElement): ARIAElement | null {
		const keys = Array.from(this._layers.keys());
		for (const key of keys) {
			const layer = this._layers.get(key);
			const index = layer.elements.findIndex((e) => e.phaserElement == object);
			if (index >= 0) return layer.elements[index];
		}
		return null;
	}
}
